import React, {useEffect, useRef, useState, useCallback} from 'react';
import './SongCardMenu.scss';

const SongCardMenu = ({actions = [], anchorRef = null, onClose}) => {
  const menuRef = useRef(null);

  const [closing, setClosing] = useState(false);
  const [posStyle, setPosStyle] = useState(null);
  const [openUpwards, setOpenUpwards] = useState(false);
  const ANIM_MS = 0; // enter animation duration
  const closeTimerRef = useRef(null);
  const rafRef = useRef(null);
  const initTimerRef = useRef(null);
  const [visible, setVisible] = useState(false);

  // Add class to body when menu opens, remove when closes
  useEffect(() => {
    document.body.classList.add('song-card-menu-open');
    return () => {
      document.body.classList.remove('song-card-menu-open');
    };
  }, []);

  // (duplicate updatePosition removed)

  useEffect(() => {
    const closedRef = { value: false };
    const startClose = () => {
      if (closedRef.value) return;
      closedRef.value = true;
      setClosing(true);
      setTimeout(() => onClose && onClose(), ANIM_MS);
    };

    // updatePosition is declared below via useCallback and used here

    const onDocClick = (e) => {
      if (!menuRef.current) return;
      if (menuRef.current.contains(e.target)) return;
      if (anchorRef && anchorRef.current && anchorRef.current.contains(e.target)) return;
      // Outside click: close with animation
      startClose();
    };

    // When scrolling, close the menu immediately (don't track position during scroll)
    const onScroll = () => startClose();
    const onWheel = () => startClose();
    // For touchstart, only close when the touch starts outside the menu/anchor
    const onTouch = (e) => {
      try {
        if (!menuRef.current) return startClose();
        const touch = e.touches && e.touches[0];
        const target = touch ? document.elementFromPoint(touch.clientX, touch.clientY) : e.target;
        if (!target) return startClose();
        if (menuRef.current.contains(target)) return; // touch inside menu -> ignore
        if (anchorRef && anchorRef.current && anchorRef.current.contains(target)) return; // touch inside anchor -> ignore
        // Check if the target is a menu item button - if so, don't close yet
        if (target.closest('.song-card-menu-item')) return;
        // otherwise close
        startClose();
      } catch (err) {
        startClose();
      }
    };

    // Delay adding click listener to avoid immediate closure from the same click that opened it
    const clickTimer = setTimeout(() => {
      document.addEventListener('click', onDocClick);
    }, 100);

    // listen to scroll/wheel to close the menu when the page or containers scroll
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('wheel', onWheel, { passive: true, capture: true });
    // Use touchstart instead of touchend to detect taps, but be more careful about menu items
    window.addEventListener('touchstart', onTouch, { passive: false, capture: true });

    return () => {
      document.removeEventListener('click', onDocClick);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('wheel', onWheel, { passive: true, capture: true });
      window.removeEventListener('touchstart', onTouch, { passive: false, capture: true });
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, [anchorRef, onClose]);

  // position menu under anchor if anchorRef provided. We store in state so we can update during scroll
  useEffect(() => {
    const init = () => {
      try {
        updatePosition();
      } catch (e) {}
    };
    // initial synchronous pass, then a RAF pass which will make the menu visible once complete
    updatePosition();
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      try {
        updatePosition();
      } catch (e) {}
      // only reveal after RAF-based layout pass to avoid visible jumps
      setVisible(true);
    });
    // and one extra timeout to catch late layout shifts
    if (initTimerRef.current) clearTimeout(initTimerRef.current);
    initTimerRef.current = setTimeout(() => {
      try {
        updatePosition();
      } catch (e) {}
    }, 120);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (initTimerRef.current) clearTimeout(initTimerRef.current);
    };
  }, [anchorRef]);

  const updatePosition = useCallback(() => {
    try {
      if (anchorRef && anchorRef.current && menuRef.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        const menu = menuRef.current;
        // Estimate menu height (if not rendered yet, fallback to 200px)
        const menuHeight = menu.offsetHeight || 200;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        // If not enough space below, open upwards
        if (spaceBelow < menuHeight && spaceAbove > menuHeight) {
          setPosStyle({
            position: 'fixed',
            top: rect.top - menuHeight - 6 + 'px',
            left: rect.left + 'px',
            zIndex: 2000,
            transformOrigin: 'bottom left',
          });
          setOpenUpwards(true);
        } else {
          setPosStyle({
            position: 'fixed',
            top: rect.bottom + 6 + 'px',
            left: rect.left + 'px',
            zIndex: 2000,
            transformOrigin: 'top left',
          });
          setOpenUpwards(false);
        }
      }
    } catch (e) {
      // ignore
    }
  }, [anchorRef]);

  const onListClick = useCallback((e) => {
    try {
      const btn = e.target.closest('[data-action-index]');
      if (!btn || !menuRef.current || !menuRef.current.contains(btn)) return;
      e.stopPropagation();
      const idx = Number(btn.getAttribute('data-action-index'));
      const a = actions[idx];
      try {
        a && a.onClick && a.onClick();
      } finally {
        setClosing(true);
        setTimeout(() => onClose && onClose(), ANIM_MS);
      }
    } catch (err) {
      // ignore
    }
  }, [actions, onClose]);

  const onListKeyDown = useCallback((e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    try {
      const btn = e.target.closest('[data-action-index]');
      if (!btn || !menuRef.current || !menuRef.current.contains(btn)) return;
      e.preventDefault();
      e.stopPropagation();
      const idx = Number(btn.getAttribute('data-action-index'));
      const a = actions[idx];
      try {
        a && a.onClick && a.onClick();
      } finally {
        setClosing(true);
        setTimeout(() => onClose && onClose(), ANIM_MS);
      }
    } catch (err) {
      // ignore
    }
  }, [actions, onClose]);

  // Compute a synchronous position from the anchor if state hasn't been populated yet.
  // Reading getBoundingClientRect synchronously here avoids a micro-flash where the menu
  // renders at (0,0) or another wrong location before effects run.
  let syncPosStyle = null;
  let syncOpenUpwards = false;
  try {
    if (!posStyle && anchorRef && anchorRef.current && menuRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const menu = menuRef.current;
      const menuHeight = menu.offsetHeight || 200;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      if (spaceBelow < menuHeight && spaceAbove > menuHeight) {
        syncPosStyle = {
          position: 'fixed',
          top: rect.top - menuHeight - 6 + 'px',
          left: rect.left + 'px',
          zIndex: 2000,
          transformOrigin: 'bottom left',
        };
        syncOpenUpwards = true;
      } else {
        syncPosStyle = {
          position: 'fixed',
          top: rect.bottom + 6 + 'px',
          left: rect.left + 'px',
          zIndex: 2000,
          transformOrigin: 'top left',
        };
        syncOpenUpwards = false;
      }
    }
  } catch (e) {
    // ignore
  }

  const effectiveStyle = posStyle || syncPosStyle || {};
  // Only use the `visible` state to control visibility so the CSS transition
  // can animate from false -> true (RAF sets visible = true after initial layout).
  const isVisible = visible;
  // Do not set opacity/pointerEvents inline (they override CSS transitions).
  const combinedStyle = Object.assign({}, effectiveStyle);

  return (
    <div
      className={`song-card-menu${openUpwards || syncOpenUpwards ? ' open-upwards' : ''}`}
      ref={menuRef}
      style={combinedStyle}
      role='menu'
      data-closing={closing ? 'true' : 'false'}
      data-visible={isVisible ? 'true' : 'false'}
    >
      <div className='song-card-menu-list' role='menu'>
        {actions.map((a, i) => (
          <button
            key={a.key || i}
            className='song-card-menu-item'
            data-action-index={i}
            onClick={(ev) => {
              ev.stopPropagation();
              ev.preventDefault();
              try {
                if (a.onClick) {
                  a.onClick(ev);
                }
              } finally {
                setClosing(true);
                setTimeout(() => onClose && onClose(), ANIM_MS);
              }
            }}
          >
            {a.icon && <span className='song-card-menu-item-icon'>{a.icon}</span>}
            <span className='song-card-menu-item-label'>{a.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default SongCardMenu;
